import Typography from '@material-ui/core/Typography'
import React, { useCallback } from 'react'
import { useTreeHandlers } from '../hooks'
import { useHoveredItemActions, useShallowHoveredItems } from '../HoveredItemStore'
import { DataNode } from '../types/types'
import { getContentItemFromNode } from '../utils/contentItemViewNodesUtils'
import { useSustainabilityWarningDialogStyles } from './useSustainabilityWarningDialogStyles'
import { WorkspaceConfirmationDialog } from './WorkspaceConfirmationDialog'

export const SustainabilityWarningDialog = () => {
    const classes = useSustainabilityWarningDialogStyles()
    const {
        showSustainabilityWarningDialog,
        sustainabilityParameters,
        hoveredNode,
    } = useShallowHoveredItems((state) => ({
        showSustainabilityWarningDialog: state.showSustainabilityWarningDialog,
        sustainabilityParameters: state.sustainabilityParameters,
        hoveredNode: state.hoveredNode,
    }))

    const {
        setShowSustainabilityWarningDialog,
    } = useHoveredItemActions()

    const { handleElementDeactivation } = useTreeHandlers()

    const handleDialogClose = useCallback(() => {
        setShowSustainabilityWarningDialog(false)
    }, [setShowSustainabilityWarningDialog])

    const handleProceed = useCallback(async () => {
        if (!hoveredNode) return

        handleDialogClose()

        try {
            await handleElementDeactivation(
                !(hoveredNode.isHidden ?? false),
                hoveredNode as DataNode
            )
        } catch (error) {
            // UI already reverted in handleElementDeactivation
        }
    }, [handleElementDeactivation, hoveredNode, handleDialogClose])

    if (!showSustainabilityWarningDialog) return null

    // Split sustainability parameters by comma to create bullet list
    const parametersList = sustainabilityParameters
        ? sustainabilityParameters.split(',').map(param => param.trim()).filter(Boolean)
        : []

    // Don't render dialog if there are no parameters to display
    if (!parametersList.length) {
        return null
    }

    // Determine if we're deactivating (hiding) sustainability content
    // Extract content item from either DataNode or ContentItemViewNodesType
    const contentItem = getContentItemFromNode(hoveredNode)

    const isSelectedSustainabilityContentItem =
        contentItem?.isSustainabilityContentItem &&
        contentItem?.isSelectedSustainabilityContentItem

    const isDeactivating = isSelectedSustainabilityContentItem && !contentItem?.isHidden
    const actionText = isDeactivating ? 'Inactivating' : 'Activating'

    return (
        <WorkspaceConfirmationDialog
            openDialog={showSustainabilityWarningDialog}
            handleDialogClose={handleDialogClose}
            dialogActionCallback={handleProceed}
            contentNode={
                <>
                    <Typography
                        className={classes.conflictMessage}
                        id="sustainability-warning-description"
                    >
                        {actionText} this content conflicts with the sustainability parameter{parametersList.length > 1 ? 's' : ''}
                        {' '}selected for this project:
                    </Typography>
                    <ul className={classes.parametersList}>
                        {parametersList.map((param, index) => (
                            <li key={`${param}-${index}`}>{param}</li>
                        ))}
                    </ul>
                </>
            }
            size="small"
            dialogHeading="Clash Detected"
            confirmText="Yes"
            confirmMessage="Are you sure you want to proceed?"
            ariaDescribedBy="sustainability-warning-description"
        />
    )
}
